"use client";
import React, { useState } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import config from "../../../../config";

const ChatbotSettings = ({
  isEditable,
  chatbotName,
  setChatbotName,
  welcomeMessage,
  setWelcomeMessage,
  disclaimerText,
  setDisclaimerText,
  logoUrl,
  setLogoUrl,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const headers = config.getHeaders();
      if (headers['Content-Type']) {
        delete headers['Content-Type'];
      }

      const response = await fetch(`${config.API_BASE_URL}/client/store/upload/logo`, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.message || "Upload failed");
      }

      if (setLogoUrl) {
        setLogoUrl(result.logo_url);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload logo: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box component="form">
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
        className="rmui-card"
      >
        {/* Top Messages - Only show if NOT editable */}
        {!isEditable && (
          <Box sx={{ mb: "20px" }}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 500, mb: "5px" }}>
              To make Changes please select Pro and above plans
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ mb: "25px" }}>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
            className="text-black"
          >
            Chatbot Settings
          </Typography>
        </Box>

        <Grid container spacing={3} direction="column">
          {/* Logo Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                border: "1px solid #E0E0E0",
                borderRadius: "12px",
                backgroundColor: "#FAFAFA",
                mb: 3
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 2 }}>
                Chatbot Logo
              </Typography>

              <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                {/* Logo Preview */}
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "12px",
                    border: "2px solid #D5D9E2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                    />
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <CloudUploadIcon sx={{ fontSize: 30, color: "#999", mb: 0.5 }} />
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: "10px" }}>
                        No logo
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Upload Section */}
                <Box sx={{ flex: 1 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading || !isEditable}
                    style={{ display: "none" }}
                    id="logo-upload-chatbot"
                  />
                  <label htmlFor="logo-upload-chatbot" style={{ display: "block" }}>
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      disabled={uploading || !isEditable}
                      sx={{ mb: 1 }}
                      size="small"
                    >
                      {uploading ? "Uploading..." : "Upload Logo"}
                    </Button>
                  </label>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Recommended: PNG or JPG, max 5MB
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Recommended size: 200x200px
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Chatbot Name */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                Chatbot Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                placeholder="Enter chatbot name"
                variant="filled"
                required
                value={chatbotName}
                onChange={(e) => setChatbotName(e.target.value)}
                disabled={!isEditable}
                inputProps={{ maxLength: 12 }}
                sx={{
                  "& .MuiInputBase-root": {
                    border: "1px solid #D5D9E2",
                    backgroundColor: "#fff",
                    borderRadius: "7px",
                  },
                  "& .MuiInputBase-root::before": { border: "none" },
                  "& .MuiInputBase-root:hover::before": { border: "none" },
                }}
              />
              {isEditable && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'text.secondary' }}>
                  {(chatbotName || "").length}/12
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Welcome Message */}
          <Grid item xs={12} sx={{ display: 'none' }}>
            <FormControl fullWidth>
              <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                Welcome Message
              </Typography>
              <TextField
                placeholder="Enter welcome message"
                variant="filled"
                multiline
                rows={3}
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                disabled={!isEditable}
                sx={{
                  "& .MuiInputBase-root": {
                    border: "1px solid #D5D9E2",
                    backgroundColor: "#fff",
                    borderRadius: "7px",
                  },
                  "& .MuiInputBase-root::before": { border: "none" },
                  "& .MuiInputBase-root:hover::before": { border: "none" },
                }}
              />
            </FormControl>
          </Grid>

          {/* Disclaimer */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography component="label" sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }} className="text-black">
                Disclaimer Text
              </Typography>
              <TextField
                placeholder="Enter disclaimer text"
                variant="filled"
                value={disclaimerText}
                onChange={(e) => setDisclaimerText(e.target.value)}
                disabled={!isEditable}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputBase-root": {
                    border: "1px solid #D5D9E2",
                    backgroundColor: "#fff",
                    borderRadius: "7px",
                  },
                  "& .MuiInputBase-root::before": { border: "none" },
                  "& .MuiInputBase-root:hover::before": { border: "none" },
                }}
              />
              {isEditable && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'text.secondary' }}>
                  {(disclaimerText || "").length}/50
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Note */}
          <Grid item xs={12} sx={{ display: 'none' }}>
            <Typography variant="body2" color="textSecondary">
              Note: If no text is entered, default disclaimer and welcome message will be applied.
            </Typography>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default ChatbotSettings;
